import { useEffect, useState } from 'react';
import useStore from '@hooks/useStore';
import { useFlowStore } from '@stores';
import { useRouter } from 'next/router';

import { allSteps as steps } from './useSteps';

interface FlowI {
  currentStep: number;
  shouldStart?: boolean;
}

const useFlow = ({ currentStep, shouldStart = true }: FlowI) => {
  const router = useRouter();
  const flowStore = useStore(useFlowStore, (state) => state);

  const [isLoading, setIsLoading] = useState(true);

  const getLastCompletedStep = (step: number): number => {
    const completedSteps = flowStore?.flow?.completedSteps || {};
    const maxStep = Object.keys(steps).length;

    if (step > maxStep) return maxStep;
    if (completedSteps[step]) {
      return getLastCompletedStep(step + 1);
    }
    return step - 1;
  };

  const setCompletedStep = (updatedSteps: Record<number, boolean>): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        flowStore.setCompletedStep(updatedSteps);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };

  const setHasFamilyMembers = (hasFamilyMembers: boolean): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        flowStore.setHasFamilyMembers(hasFamilyMembers);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };

  const isCurrentStepNext = (): boolean => {
    const lastCompletedStep = getLastCompletedStep(1);
    const nextStep = lastCompletedStep + 1;

    return currentStep === nextStep;
  };

  const resetFlow = (): void => {
    flowStore.resetFlow();
  };

  const determineNextStep = (lastCompletedStep: number): number => {
    const maxStep = Object.keys(steps).length;
    return Math.min(lastCompletedStep + 1, maxStep);
  };

  useEffect(() => {
    if (!flowStore) return;
    if (!shouldStart) return;

    setIsLoading(true);

    const { isInitialized } = flowStore;
    const lastCompletedStep = getLastCompletedStep(1);
    const nextStep = determineNextStep(lastCompletedStep);
    if (!steps[nextStep]) {
      console.error(`[useFlow] Error: nextStep (${nextStep}) is not defined.`);
      setIsLoading(false);
      return;
    }

    const currentPath = router.pathname;
    const targetStepPath = `/${steps[nextStep]}`;
    const currentStepPath = `/${steps[currentStep]}`;

    let toRedirect = '';

    if (!isInitialized && currentStep === 1) {
      flowStore.setInitialized();
    } else if (currentStep > nextStep) {
      toRedirect = targetStepPath;
    } else if (currentStep <= lastCompletedStep && currentPath === currentStepPath) {
      setIsLoading(false);
      return;
    } else if (currentStep < nextStep && currentPath !== targetStepPath) {
      toRedirect = targetStepPath;
    }

    if (toRedirect) {
      router.push(toRedirect).finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [currentStep, flowStore, router, shouldStart]);

  return {
    getInitialized: flowStore?.isInitialized,
    setCompletedStep,
    setHasFamilyMembers,
    getLastCompletedStep,
    isCurrentStepNext,
    isLoading,
    resetFlow,
  };
};

export default useFlow;
